import { ComponentProps, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { GlobeCrossedOut, Refresh, RefreshCrossedOut } from "@/assets/icons/ds";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";
import { NamespaceKey } from "@/utils/translationKey";

const stateIcons = {
  locked: GlobeCrossedOut,
  pausing: RefreshCrossedOut,
  renewing: Refresh,
};

/** Next-bill and pause sublines; narrowed so Trans can type its key union. */
type StatusDateLineKey = Extract<
  NamespaceKey<"portal">,
  "common.nextBill" | `cockpit.managementTile.${string}.status.pausing`
>;

/** A status box line with the date in bold, e.g. "Next bill on <b>1.11.</b>". */
export const StatusDateLine = ({
  i18nKey,
  date,
}: {
  i18nKey: StatusDateLineKey;
  date: string;
}) => {
  const { t } = useTranslation("portal");

  return (
    <Trans
      t={t}
      i18nKey={i18nKey}
      components={[<span key="0" className="font-semibold" />]}
      values={{ date }}
    />
  );
};

export type StatusSummaryBoxProps = ComponentProps<typeof StatusSummaryBox>;

/**
 * The grey box at the top of the subscription and Watch SIM status cards:
 * a state icon, the heading, an optional line beneath it and the progress
 * through the current billing period.
 */
export const StatusSummaryBox = ({
  state,
  heading,
  subline,
  progress,
}: {
  state: keyof typeof stateIcons;
  heading: ReactNode;
  subline?: ReactNode;
  /** Percentage of the billing period elapsed; empty bar when undefined. */
  progress?: number;
}) => {
  const Icon = stateIcons[state];

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4">
      <div className="flex w-full items-start gap-4">
        <span className="shrink-0 text-foreground-primary">
          <Icon className="size-6" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-foreground-primary">
          <p className="truncate-safe text-body-base font-semibold leading-none">
            {heading}
          </p>
          {subline && (
            <p className="truncate-safe text-body-xs leading-none">{subline}</p>
          )}
        </div>
      </div>
      <DireProgressBar value={progress} />
    </div>
  );
};
